import React from 'react';
import PropTypes from 'prop-types';
import { Range } from 'rc-slider';
import geometric from '../rc-slider/geometric';
import qs from 'qs';
import Spinner from 'components/spinner/spinner';
import { withTranslator } from 'tradera-lang/translate';
import * as gtm from 'tradera-apps/tradera/components/discover/helpers/search-gtm';

import 'components/rc-slider/rc-slider.scss';
import './slider-horizontal.scss';

// TOOD: Rename component to "range"? To avoid confusion with the price-slider container

class PriceRange extends React.Component {
    constructor(props) {
        super(props);
        this.navigateEvent = props.navigateEvent;
        this.state = {
            //set starting position for handle
            usedSlider: false,
            min: props.handleStart || 1,
            max: props.handleEnd || '',
            loading: false,
            lastSearchMin: props.handleStart || 1,
            lastSearchMax:
                props.handleEnd === props.rangeEnd ? null : props.handleEnd
        };
        this.formElement = null;
    }

    setFormReference = el => {
        this.formElement = el;
    };

    /**
     * @description Handles min value changing in price slider
     * @param {object} event syntehtic event contains value `event.target.value`
     * @memberof PriceRange
     */
    handleMinChange = event => {
        this.setMin(event.target.value);
    };

    setMin = value => {
        let min = value;
        if (min === '' || isNaN(min)) {
            min = min === '' ? 1 : this.state.min;
        } else {
            min = parseInt(min, 10);
        }
        this.setState({
            min
        });
        return min;
    };

    /**
     * @description Handles max value changing in price slider
     * @param {object} event syntehtic event contains value `event.target.value`
     * @memberof PriceRange
     */
    handleMaxChange = event => {
        const max = this.setMax(event.target.value);
        if (max > 4999) {
            event.currentTarget.blur();
        }
    };

    setMax = value => {
        const { rangeEnd } = this.props;
        let max = value;
        if (max === '' || isNaN(max)) {
            max = max === '' ? rangeEnd : this.state.max;
        } else {
            max = parseInt(max, 10);
        }
        max = Math.min(max, 5000);
        this.setState({
            max
        });
        return max;
    };

    stateHasChangedSinceStart = (inputMinValue, inputMaxValue) => {
        return (
            inputMinValue !== this.state.lastSearchMin ||
            inputMaxValue !== this.state.lastSearchMax
        );
    };

    gotoNewURL = (min, max) => {
        const { handlePriceFilterChange } = this.props;

        const minInput = this.formElement.querySelector('.price-range__min');
        const maxInput = this.formElement.querySelector('.price-range__max');
        const inputs = [minInput, maxInput];

        // Get values from inputs if not provided as arguments
        let values = [
            min === undefined ? parseInt(minInput.value, 10) : min,
            max === undefined ? parseInt(maxInput.value, 10) : max
        ];
        if (!this.stateHasChangedSinceStart(...values)) {
            return;
        }

        for (let index = 0; index < inputs.length; index++) {
            if (document.activeElement === inputs[index]) {
                return;
            }
        }

        const search = () => {
            const formElements = Array.from(this.formElement.elements);
            const inputs = formElements.filter(el => {
                return el.tagName.toLocaleLowerCase() === 'input';
            });

            const urlParamsAsObject = inputs.reduce((acc, cur) => {
                if (cur.value) {
                    // Only keep params with value
                    acc[cur.name] = cur.value;
                }
                return acc;
            }, {});

            if (handlePriceFilterChange) {
                handlePriceFilterChange(urlParamsAsObject);
                gtm.trackPriceFilter(
                    urlParamsAsObject.fromPrice,
                    urlParamsAsObject.toPrice
                );
                this.setState({
                    lastSearchMin: parseInt(urlParamsAsObject['fromPrice'], 10),
                    lastSearchMax:
                        parseInt(urlParamsAsObject['toPrice'], 10) || null,
                    loading: false
                });
            } else {
                const url =
                    this.formElement.action +
                    '?' +
                    qs.stringify(urlParamsAsObject);

                $(document).trigger(this.navigateEvent, { href: url }); // TODO: Change to a regular event when we support it
            }
        };

        this.setState({
            loading: true
        });
        setTimeout(search, 1); // Hack to make sure its called after the handleValuesChanging::setState
    };

    handleValuesChanging = ([min, max]) => {
        this.setMin(min);
        this.setMax(max);
    };

    renderUrlParamsAsElements = () => {
        const allUrlParams = qs.parse(window.location.search, {
            ignoreQueryPrefix: true
        });
        const reservedParams = ['fromPrice', 'toPrice'];

        if (Object.keys(allUrlParams).length > 0) {
            const relevantUrlParams = Object.keys(allUrlParams).filter(
                paramName => {
                    return reservedParams.indexOf(paramName) < 0;
                }
            );
            return relevantUrlParams.map(paramName => {
                return (
                    <input
                        type="hidden"
                        name={paramName}
                        key={paramName}
                        value={allUrlParams[paramName]}
                    />
                );
            });
        }
    };

    inputMinLength = () => {
        return '0' === this.state.min.toString().length
            ? 1
            : this.state.min.toString().length;
    };

    inputMaxLength = () => {
        return '0' === this.state.max.toString().length
            ? 1
            : this.state.max.toString().length;
    };

    handleInputBlur = () => {
        if (this.navigateEvent || this.props.handlePriceFilterChange) {
            this.gotoNewURL();
        }
    };

    handleSliderInteraction = ([min, max]) => {
        const { handleStart, handleEnd, handlePriceFilterChange } = this.props;
        if (this.navigateEvent || handlePriceFilterChange) {
            // Check it values differ from what is currently set
            if (handleStart !== min || handleEnd !== max) {
                this.gotoNewURL(min, max);
            }
        }
    };

    componentDidUpdate(prevProps) {
        // Update min and max values when updated through props
        if (prevProps.handleStart !== this.props.handleStart) {
            this.setMin(this.props.handleStart);
        }
        if (prevProps.handleEnd !== this.props.handleEnd) {
            this.setMax(this.props.handleEnd);
        }
    }

    render() {
        const {
            algorithm,
            rangeStart,
            rangeEnd,
            handleStart,
            handleEnd,
            t
        } = this.props;
        const { min, max } = this.state;
        const isMax = max >= rangeEnd;
        const limitedMax = isMax ? rangeEnd : max;

        return (
            <form
                className={
                    this.state.loading
                        ? 'price-range__form price-range__form--loading'
                        : 'price-range__form'
                }
                method="get"
                action={window.location.pathname}
                ref={this.setFormReference}>
                {this.renderUrlParamsAsElements()}
                {this.state.loading && (
                    <Spinner className="price-range__spinner" />
                )}
                <span className="price-range__value">
                    <input
                        type="text"
                        title="Min"
                        className="price-range__min"
                        size={this.inputMinLength()}
                        value={min}
                        name="fromPrice"
                        onChange={this.handleMinChange}
                        onBlur={this.handleInputBlur}
                        autoComplete="off"
                    />
                    <label
                        className="price-range__appendix price-range__appendix--min"
                        htmlFor="price-range__min">
                        {t('priceRange_price')}
                    </label>
                </span>
                <span className="price-range__value">
                    {!isMax ? (
                        <label
                            className="price-range__appendix price-range__appendix--max"
                            htmlFor="price-range__max">
                            {t('priceRange_price')}{' '}
                            {max === rangeEnd ? '+' : ''}
                        </label>
                    ) : (
                        ''
                    )}
                    <input
                        type="text"
                        title="Max"
                        className="price-range__max"
                        size={this.inputMaxLength()}
                        name="toPrice"
                        value={isMax ? '' : max}
                        onChange={this.handleMaxChange}
                        onBlur={this.handleInputBlur}
                        placeholder={t('priceRange_all')}
                        autoComplete="off"
                    />
                </span>
                <div className="price-range__slider-wrapper">
                    <Range
                        className={`price-range__slider`}
                        min={rangeStart}
                        max={rangeEnd}
                        value={[min, limitedMax]}
                        defaultValue={[handleStart, handleEnd]}
                        algorithm={algorithm || geometric}
                        onChange={this.handleValuesChanging}
                        onAfterChange={this.handleSliderInteraction}
                    />
                </div>
            </form>
        );
    }
}

PriceRange.propTypes = {
    handleStart: PropTypes.number.isRequired,
    handleEnd: PropTypes.number.isRequired,
    handlePriceFilterChange: PropTypes.func,
    rangeStart: PropTypes.number.isRequired,
    rangeEnd: PropTypes.number.isRequired,
    algorithm: PropTypes.object,
    navigateEvent: PropTypes.string
};

// export default PriceRange;
export { PriceRange };
export default withTranslator(PriceRange);
